import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Mail, Phone } from "lucide-react";
import { Logo } from "./Logo";
import { PHONE, PHONE_HREF, EMAIL, EMAIL_HREF } from "@/lib/images";

const LINKS = [
  { to: "/", label: "Home", testid: "nav-home-link" },
  { to: "/about", label: "About", testid: "nav-about-link" },
  { to: "/services", label: "Services", testid: "nav-services-link" },
  { to: "/#quiz", label: "Match Quiz", testid: "nav-quiz-link", hash: true },
  { to: "/gallery", label: "Gallery", testid: "nav-gallery-link" },
  { to: "/faq", label: "FAQ", testid: "nav-faq-link" },
  { to: "/contact", label: "Contact", testid: "nav-contact-link" },
];

const linkClass = (isActive) =>
  `relative text-base font-semibold transition-colors duration-200 after:absolute after:-bottom-1.5 after:left-0 after:h-0.5 after:rounded-full after:bg-brand after:transition-all after:duration-300 ${
    isActive ? "text-brand after:w-full" : "text-espresso after:w-0 hover:text-brand hover:after:w-full"
  }`;

export const Navbar = () => {
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 border-b border-espresso/10 bg-cream/90 backdrop-blur-md">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
        <Link to="/" data-testid="nav-logo-link" aria-label="Off Our Rockers Seniors Care home" onClick={() => setOpen(false)}>
          <Logo />
        </Link>

        <nav className="hidden items-center gap-5 lg:flex" aria-label="Primary">
          {LINKS.map((l) =>
            l.hash ? (
              <Link key={l.to} to={l.to} data-testid={l.testid} className={linkClass(false)}>
                {l.label}
              </Link>
            ) : (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={l.testid}
                className={({ isActive }) => linkClass(isActive)}
              >
                {l.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="hidden items-center gap-4 lg:flex">
          <a
            href={PHONE_HREF}
            data-testid="nav-phone-link"
            className="flex items-center gap-2 whitespace-nowrap text-base font-bold text-espresso transition-colors hover:text-brand"
          >
            <Phone className="h-4 w-4 shrink-0 text-brand" />
            {PHONE}
          </a>
          <Link to="/contact" data-testid="nav-book-consultation-button" className="btn-primary !px-6 !py-2.5 !text-base">
            Book Free Consultation
          </Link>
        </div>

        <button
          data-testid="nav-mobile-menu-button"
          className="rounded-lg p-2 text-espresso transition-colors hover:bg-sand lg:hidden"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
        >
          {open ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
        </button>
      </div>

      {open && (
        <nav className="border-t border-espresso/10 bg-cream px-4 pb-6 pt-2 lg:hidden" aria-label="Mobile">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`mobile-${l.testid}`}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `block rounded-lg px-3 py-3.5 text-lg font-semibold transition-colors ${
                  isActive && !l.hash ? "bg-sand text-brand" : "text-espresso hover:bg-sand"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <a
            href={PHONE_HREF}
            data-testid="mobile-nav-phone-link"
            className="mt-2 flex items-center gap-2 px-3 py-3 text-lg font-bold text-espresso"
          >
            <Phone className="h-5 w-5 shrink-0 text-brand" />
            {PHONE}
          </a>
          <a
            href={EMAIL_HREF}
            data-testid="mobile-nav-email-link"
            className="flex items-center gap-2 break-all px-3 py-3 text-base font-bold text-espresso"
          >
            <Mail className="h-5 w-5 shrink-0 text-brand" />
            {EMAIL}
          </a>
          <Link
            to="/contact"
            data-testid="mobile-nav-book-consultation-button"
            onClick={() => setOpen(false)}
            className="btn-primary mt-3 w-full"
          >
            Book Free Consultation
          </Link>
        </nav>
      )}
    </header>
  );
};
